import { useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAnnouncementStore, AnnouncementType, startAnnouncementDemo } from '@/stores/announcementStore'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'

const typeConfig: Record<AnnouncementType, { icon: string; accent: string; textClass: string }> = {
  jackpot: {
    icon: '🏆',
    accent: 'from-amber-500/20 via-amber-500/10 to-amber-500/5 dark:from-amber-500/25 dark:via-amber-900/40 dark:to-amber-500/10',
    textClass: 'text-amber-800 dark:bg-gradient-to-r dark:from-amber-200 dark:via-yellow-100 dark:to-amber-300 dark:bg-clip-text dark:text-transparent',
  },
  winner: {
    icon: '🎉',
    accent: 'from-amber-500/15 via-emerald-500/10 to-amber-500/15 dark:from-amber-500/20 dark:via-emerald-500/15 dark:to-amber-500/20',
    textClass: 'text-emerald-800 dark:text-emerald-200',
  },
  system: {
    icon: '📢',
    accent: 'from-blue-500/20 via-blue-500/10 to-blue-500/5 dark:from-blue-500/25 dark:via-blue-900/40 dark:to-blue-500/10',
    textClass: 'text-blue-800 dark:text-blue-200',
  },
  promotion: {
    icon: '✨',
    accent: 'from-purple-500/20 via-purple-500/10 to-purple-500/5 dark:from-purple-500/25 dark:via-purple-900/40 dark:to-purple-500/10',
    textClass: 'text-purple-800 dark:text-purple-200',
  },
}

const CONFETTI_COLORS = ['#F5D78E', '#D4A843', '#34D399', '#FCD34D', '#A78BFA', '#F472B6']

const AUTO_DISMISS_MS = 5000

/** Small confetti particles that burst around the banner for winner announcements */
function MiniConfetti({ id }: { id: string }) {
  const particles = useMemo(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      // Spread across full width
      x: 4 + (i / 13) * 92,
      // Stagger entrance
      delay: Math.random() * 0.4,
      duration: 1.4 + Math.random() * 0.6,
      // Tiny particles
      size: 3 + Math.random() * 3,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      // Horizontal drift
      drift: (Math.random() - 0.5) * 24,
      // Vertical travel distance
      travel: 14 + Math.random() * 18,
      // Slight rotation for non-round shapes
      rotation: Math.random() * 360,
      // Some are circles, some tiny rectangles
      isRect: Math.random() > 0.5,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  )

  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{
            left: `${p.x}%`,
            top: '-2px',
            width: p.size,
            height: p.isRect ? p.size * 1.6 : p.size,
            backgroundColor: p.color,
            borderRadius: p.isRect ? 1 : '50%',
          }}
          initial={{ y: 0, x: 0, opacity: 0, scale: 0, rotate: 0 }}
          animate={{
            y: [0, p.travel * 0.6, p.travel, p.travel + 6],
            x: [0, p.drift * 0.4, p.drift * 0.8, p.drift],
            opacity: [0, 0.9, 0.9, 0],
            scale: [0, 1, 1, 0.3],
            rotate: [0, p.rotation],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: 'easeOut',
            times: [0, 0.3, 0.7, 1],
          }}
        />
      ))}
    </div>
  )
}

export function GlobalAnnouncementBanner() {
  const current = useAnnouncementStore((s) => s.current)
  const dismiss = useAnnouncementStore((s) => s.dismiss)

  // Start demo on mount
  useEffect(() => {
    startAnnouncementDemo()
  }, [])

  // Auto-dismiss after timeout
  useEffect(() => {
    if (!current) return
    const timer = setTimeout(dismiss, AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [current, dismiss])

  return (
    <AnimatePresence>
      {current && (() => {
        const config = typeConfig[current.type]
        const isWinner = current.type === 'winner'

        return (
          <motion.div
            key={current.id}
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="absolute top-full left-0 right-0 z-30 overflow-visible"
          >
            <div className="relative overflow-hidden bg-background border-b border-foreground/[0.06]">
              {/* Colored accent gradient background */}
              <div className={cn('absolute inset-0 bg-gradient-to-r', config.accent)} />

              {/* Subtle shimmer sweep for winner */}
              {isWinner && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.08] to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '200%' }}
                  transition={{ duration: 1.5, delay: 0.2, ease: 'easeInOut' }}
                />
              )}

              {/* Confetti particles for winner — allowed to overflow above */}
              {isWinner && <MiniConfetti id={current.id} />}

              {/* Content row */}
              <div className="relative flex items-center h-10 px-4 gap-2.5">
                <motion.span
                  className="text-sm flex-shrink-0"
                  initial={isWinner ? { scale: 0, rotate: -20 } : false}
                  animate={isWinner ? { scale: 1, rotate: 0 } : undefined}
                  transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.1 }}
                >
                  {config.icon}
                </motion.span>

                <span className={cn('text-xs font-semibold flex-1 leading-snug truncate', config.textClass)}>
                  {current.message}
                </span>

                <button
                  onClick={dismiss}
                  className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center hover:bg-foreground/10 active:bg-foreground/15 transition-colors"
                >
                  <X className="w-3 h-3 text-foreground/40" />
                </button>
              </div>

              {/* Progress bar at bottom */}
              <motion.div
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{ duration: AUTO_DISMISS_MS / 1000, ease: 'linear' }}
                className="absolute bottom-0 left-0 right-0 h-[2px] origin-left bg-foreground/15"
              />
            </div>
          </motion.div>
        )
      })()}
    </AnimatePresence>
  )
}
